import { formatDuration } from 'date-fns';

interface Duration {
  years?: number;
  months?: number;
  weeks?: number;
  days?: number;
  hours?: number;
  minutes?: number;
  seconds?: number;
}

/**
 * Parse ISO 8601 duration string into a Duration object
 * Supports formats like: PT1H, P1D, P1W, PT30M, PT1H30M, P1DT2H30M, etc.
 *
 * @param isoDuration - ISO 8601 duration string (e.g., "-PT1H", "P2D", "PT30M")
 * @returns Object with parsed duration and direction
 */
function parseISO8601Duration(isoDuration: string): {
  duration: Duration;
  isNegative: boolean;
} {
  const isNegative = isoDuration.startsWith('-');
  const cleanDuration = isNegative ? isoDuration.slice(1) : isoDuration;

  const duration: Duration = {};

  // ISO 8601 duration regex pattern
  // P[n]Y[n]M[n]DT[n]H[n]M[n]S or P[n]W
  const pattern =
    /^P(?:(\d+(?:\.\d+)?)Y)?(?:(\d+(?:\.\d+)?)M)?(?:(\d+(?:\.\d+)?)W)?(?:(\d+(?:\.\d+)?)D)?(?:T(?:(\d+(?:\.\d+)?)H)?(?:(\d+(?:\.\d+)?)M)?(?:(\d+(?:\.\d+)?)S)?)?$/;

  const match = cleanDuration.match(pattern);

  if (!match) {
    throw new Error(`Invalid ISO 8601 duration: ${isoDuration}`);
  }

  const [, years, months, weeks, days, hours, minutes, seconds] = match;

  if (years) duration.years = parseFloat(years);
  if (months) duration.months = parseFloat(months);
  if (weeks) duration.weeks = parseFloat(weeks);
  if (days) duration.days = parseFloat(days);
  if (hours) duration.hours = parseFloat(hours);
  if (minutes) duration.minutes = parseFloat(minutes);
  if (seconds) duration.seconds = parseFloat(seconds);

  return { duration, isNegative };
}

/**
 * Convert ISO 8601 duration to human-readable format for appointments
 * Uses date-fns formatDuration for robust, localized formatting
 *
 * Examples:
 * "-PT1H" → "1 hour before appointment"
 * "-P2D" → "2 days before appointment"
 * "PT30M" → "30 minutes after appointment"
 * "-P1DT2H" → "1 day 2 hours before appointment"
 * "-PT0M" or "PT0M" → "at appointment time"
 *
 * @param isoDuration - ISO 8601 duration string
 * @returns Human-readable duration string with appointment context
 */
export function formatAppointmentDuration(isoDuration: string): string {
  if (!isoDuration || isoDuration === '0' || isoDuration === 'PT0M' || isoDuration === 'PT0H') {
    return 'at appointment time';
  }

  try {
    const { duration, isNegative } = parseISO8601Duration(isoDuration);

    // Check if duration is essentially zero
    const hasNonZeroValue = Object.values(duration).some(value => value && value > 0);
    if (!hasNonZeroValue) {
      return 'at appointment time';
    }

    // Use date-fns formatDuration for clean, localized formatting
    const formattedDuration = formatDuration(duration, {
      format: getOptimalFormat(duration),
      delimiter: ' ',
    });

    if (!formattedDuration) {
      return 'at appointment time';
    }

    const direction = isNegative ? 'before' : 'after';
    return `${formattedDuration} ${direction} appointment`;
  } catch (error) {
    console.warn('Failed to parse ISO 8601 duration:', isoDuration, error);
    return isoDuration; // Fallback to original string
  }
}

/**
 * Determine the optimal format array for formatDuration based on the duration values
 * This helps avoid showing unnecessary zero values and prioritizes the most significant units
 */
function getOptimalFormat(duration: Duration): (keyof Duration)[] {
  const format: (keyof Duration)[] = [];

  // Add units in order of significance, but only if they have values or are needed for context
  if (duration.years) format.push('years');
  if (duration.months) format.push('months');
  if (duration.weeks) format.push('weeks');
  if (duration.days) format.push('days');
  if (duration.hours) format.push('hours');
  if (duration.minutes) format.push('minutes');
  if (duration.seconds) format.push('seconds');

  // If no specific format determined, default to a reasonable subset
  if (format.length === 0) {
    return ['days', 'hours', 'minutes'];
  }

  // Limit to 2-3 most significant units for readability
  return format.slice(0, 3);
}

/**
 * Batch convert multiple ISO 8601 durations to human-readable format
 * Useful for processing lists of appointments, forms, or reminders
 */
export function formatAppointmentDurations(isoDurations: string[]): string[] {
  return isoDurations.map(formatAppointmentDuration);
}

/**
 * Check if an ISO 8601 duration represents "immediate" timing (zero duration)
 */
export function isImmediateTiming(isoDuration: string): boolean {
  if (!isoDuration || isoDuration === '0') return true;

  try {
    const { duration } = parseISO8601Duration(isoDuration);
    return !Object.values(duration).some(value => value && value > 0);
  } catch {
    return false;
  }
}

/**
 * Get the direction of a duration (before/after appointment)
 */
export function getDurationDirection(isoDuration: string): 'before' | 'after' | 'immediate' {
  if (isImmediateTiming(isoDuration)) return 'immediate';
  return isoDuration.startsWith('-') ? 'before' : 'after';
}

export type GuidedTimingUnit = 'minutes' | 'hours' | 'days' | 'weeks';
export type GuidedTimingDirection = 'before' | 'after';

/**
 * A send offset expressed the way users pick it: "<quantity> <unit> before/after appointment".
 */
export interface GuidedTiming {
  quantity: number;
  unit: GuidedTimingUnit;
  direction: GuidedTimingDirection;
}

/** Forms/reminders may only be scheduled within this window around the appointment. */
export const MAX_SCHEDULING_WINDOW_DAYS = 30;

export function guidedTimingToDays(quantity: number, unit: GuidedTimingUnit): number {
  switch (unit) {
    case 'minutes':
      return quantity / (60 * 24);
    case 'hours':
      return quantity / 24;
    case 'days':
      return quantity;
    case 'weeks':
      return quantity * 7;
  }
}

export function isGuidedTimingWithinWindow(quantity: number, unit: GuidedTimingUnit): boolean {
  return guidedTimingToDays(quantity, unit) <= MAX_SCHEDULING_WINDOW_DAYS;
}

/**
 * Convert guided inputs to an ISO 8601 duration offset. Weeks are stored as
 * days (2 weeks -> P14D) to match how offsets have historically been persisted.
 */
export function guidedTimingToISO({ quantity, unit, direction }: GuidedTiming): string {
  let iso: string;
  switch (unit) {
    case 'minutes':
      iso = `PT${quantity}M`;
      break;
    case 'hours':
      iso = `PT${quantity}H`;
      break;
    case 'days':
      iso = `P${quantity}D`;
      break;
    case 'weeks':
      iso = `P${quantity * 7}D`;
      break;
  }
  return direction === 'before' ? `-${iso}` : iso;
}

const COMPACT_UNIT_SUFFIX: Record<GuidedTimingUnit, string> = {
  minutes: 'm',
  hours: 'h',
  days: 'd',
  weeks: 'w',
};

interface SendOffsetToken {
  token: string;
  direction: 'before' | 'after' | 'immediate';
  /** Distance from the appointment in days, for chronological ordering. */
  sortDays: number;
}

const toSendOffsetToken = (offset: string): SendOffsetToken => {
  if (isImmediateTiming(offset)) {
    return { token: 'at appt time', direction: 'immediate', sortDays: 0 };
  }

  const direction = getDurationDirection(offset) as 'before' | 'after';
  const parsed = parseGuidedTiming(offset);
  // Only compact when the guided form is faithful — parseGuidedTiming keeps
  // just the leading unit of multi-unit offsets ('-P1DT2H' -> 1 day), and a
  // lossy "1d" would misstate when the send actually goes out.
  if (parsed && guidedTimingToISO(parsed) === offset) {
    return {
      token: `${parsed.quantity}${COMPACT_UNIT_SUFFIX[parsed.unit]}`,
      direction,
      sortDays: guidedTimingToDays(parsed.quantity, parsed.unit),
    };
  }

  // Not expressible as a single guided unit (e.g. '-P1DT2H'): fall back to the
  // long form minus the direction words, still sorted by real distance.
  const token = formatAppointmentDuration(offset).replace(/ (?:before|after) appointment$/, '');
  let sortDays = Number.POSITIVE_INFINITY;
  try {
    const { duration } = parseISO8601Duration(offset);
    sortDays =
      (duration.years ?? 0) * 365 +
      (duration.months ?? 0) * 30 +
      (duration.weeks ?? 0) * 7 +
      (duration.days ?? 0) +
      (duration.hours ?? 0) / 24 +
      (duration.minutes ?? 0) / (60 * 24) +
      (duration.seconds ?? 0) / 86400;
  } catch {
    // Unparseable strings sort to the end of their direction group.
  }
  return { token, direction, sortDays };
};

/**
 * Compact one-line summary of a rule's send offsets for chips and other dense
 * surfaces: "7d · 1d · 2h before", "1d before · 5m after", "at appt time".
 * Sends are ordered chronologically (furthest-before first, furthest-after
 * last) and the before/after word is written once per direction group.
 * Empty offsets are ignored; an empty result means nothing to summarize.
 */
export function summarizeSendOffsets(offsets: string[]): string {
  const tokens = offsets.filter(Boolean).map(toSendOffsetToken);

  const before = tokens
    .filter(entry => entry.direction === 'before')
    .sort((a, b) => b.sortDays - a.sortDays);
  const immediate = tokens.filter(entry => entry.direction === 'immediate');
  const after = tokens
    .filter(entry => entry.direction === 'after')
    .sort((a, b) => a.sortDays - b.sortDays);

  const parts: string[] = [];
  if (before.length > 0) parts.push(`${before.map(entry => entry.token).join(' · ')} before`);
  if (immediate.length > 0) parts.push(immediate.map(entry => entry.token).join(' · '));
  if (after.length > 0) parts.push(`${after.map(entry => entry.token).join(' · ')} after`);
  return parts.join(' · ');
}

/**
 * Parse an ISO 8601 offset back into guided inputs. Returns null for zero
 * offsets and durations that don't map onto a single quantity + unit (those
 * can't be represented in the guided fields). Day counts divisible by 7 and
 * larger than one week come back as weeks.
 */
export function parseGuidedTiming(isoDuration: string | null | undefined): GuidedTiming | null {
  if (!isoDuration || isImmediateTiming(isoDuration)) return null;

  const direction: GuidedTimingDirection = isoDuration.startsWith('-') ? 'before' : 'after';
  const cleanISO = isoDuration.startsWith('-') ? isoDuration.slice(1) : isoDuration;

  const match = cleanISO.match(/^P(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?)?$/);
  if (!match) return null;

  const [, days, hours, minutes] = match;

  if (days) {
    const dayCount = parseInt(days, 10);
    if (dayCount % 7 === 0 && dayCount > 7) {
      return { quantity: dayCount / 7, unit: 'weeks', direction };
    }
    return { quantity: dayCount, unit: 'days', direction };
  }
  if (hours) {
    return { quantity: parseInt(hours, 10), unit: 'hours', direction };
  }
  if (minutes) {
    return { quantity: parseInt(minutes, 10), unit: 'minutes', direction };
  }

  return null;
}
